/**
 * TutorStudentsScreen — l'onglet « Mes élèves » (`students`) : la liste et, au large, la fiche ouverte
 * à côté (`<TutorStudentsTwoPane>`).
 *
 * La disposition se tranche sur la largeur de l'onglet, au seuil du gabarit (`TWO_PANE_FROM`), lue comme
 * lui la lit (le conteneur) — le modèle est `StudentTutoringTwoPane` :
 *  - en deçà, un écran à la fois : toucher un élève pousse sa fiche (`student-profil`, `withActions`), sa
 *    pastille de non-lus ouvre `messenger` (l'ancienne ligne faisait `navigate("messenger")`) ;
 *  - au large, toucher un élève ouvre SA fiche à droite, sans retour, sur l'onglet gardé ici (il survit
 *    au changement d'élève) ; sa pastille l'ouvre sur Messages. Rien d'ouvert, le volet le dit.
 *
 * « Activité » ouvre la génération commune, le choix des élèves d'emblée.
 */
import { useNavigation } from "@react-navigation/native"
import React, { useCallback, useMemo, useState } from "react"
import { type LayoutChangeEvent, View } from "react-native"
import { useSelector } from "react-redux"

import { TutorStudentsTwoPane, TWO_PANE_FROM } from "../../../../../../design-system/tutor"
import { tutorStudentsViewModel } from "../../../../view-models/tutor/Students/tutorStudentsViewModel"
import { type UITutorStudent, type UITutorStudentTab } from "../../../types/tutorEleves"
import { type TutorStudentId } from "./tutorStudentTabs"
import { useTutorGenerateActivity } from "./useTutorGenerateActivity"
import { useTutorStudentProfile } from "./useTutorStudentProfile"
import { useTutorStudentsData } from "./useTutorStudentsData"

type StackNavigation = {
    push: (route: string, params?: object) => void
    navigate: (route: string, params?: object) => void
}

export function TutorStudentsScreen() {
    const navigation = useNavigation() as unknown as StackNavigation
    const vm = useSelector(tutorStudentsViewModel)
    const data = useTutorStudentsData()
    const generation = useTutorGenerateActivity()
    const [split, setSplit] = useState(false)
    const [openStudentId, setOpenStudentId] = useState<TutorStudentId | null>(null)
    const [tab, setTab] = useState<UITutorStudentTab>("overview")
    const onLayout = useCallback(
        (event: LayoutChangeEvent) => setSplit(event.nativeEvent.layout.width >= TWO_PANE_FROM),
        []
    )
    const open = split && openStudentId != null
    const fiche = useTutorStudentProfile({
        studentId: open ? openStudentId : null,
        tab,
        onChangeTab: setTab,
        visible: open,
        openGeneration: generation.open,
    })

    const onOpenProfile = useCallback(
        (student: UITutorStudent) => {
            if (split) setOpenStudentId(student.id)
            else navigation.push("student-profil", { studentId: student.id, withActions: true })
        },
        [navigation, split]
    )
    const onOpenMessages = useCallback(
        (student: UITutorStudent) => {
            if (!split) return navigation.navigate("messenger", { studentId: student.id })
            setOpenStudentId(student.id)
            setTab("messages")
        },
        [navigation, split]
    )
    const openGeneration = generation.open
    // La liste ne change pas quand la fiche d'à côté bouge : ses props sont gardées (la page est mémoïsée).
    const students = useMemo(
        () => ({
            students: vm.students,
            onOpenProfile,
            onOpenMessages,
            onGenerateForStudents: openGeneration != null ? () => openGeneration({ kind: "students" }) : undefined,
            refreshing: vm.refreshing,
            onRefresh: data.refresh,
            loading: vm.loading,
            error: vm.error,
            onRetry: data.refresh,
        }),
        [vm, onOpenProfile, onOpenMessages, openGeneration, data.refresh]
    )

    return (
        <View style={{ flex: 1 }} onLayout={onLayout}>
            <TutorStudentsTwoPane
                students={students}
                fiche={open ? fiche.page : null}
                openStudentId={open ? openStudentId : null}
                testID="tutor-students"
            />
            {open && fiche.overlay}
            {generation.overlay}
        </View>
    )
}
